import { createContext, useContext, useState, useEffect } from 'react';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const savedWishlist = localStorage.getItem('ethereal_wishlist');
    if (savedWishlist) {
      try {
        setWishlist(JSON.parse(savedWishlist));
      } catch (e) {
        console.error('Error loading wishlist:', e);
        localStorage.removeItem('ethereal_wishlist');
      }
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('ethereal_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const addToWishlist = (artwork) => {
    setWishlist((prev) => {
      if (prev.some((item) => item.id === artwork.id)) {
        return prev;
      }
      return [...prev, artwork];
    });
  };

  const removeFromWishlist = (artworkId) => {
    setWishlist((prev) => prev.filter((item) => item.id !== artworkId));
  };

  const isInWishlist = (artworkId) => {
    return wishlist.some((item) => item.id === artworkId);
  };

  const toggleWishlist = (artwork) => {
    if (isInWishlist(artwork.id)) {
      removeFromWishlist(artwork.id);
      return false;
    } else {
      addToWishlist(artwork);
      return true;
    }
  };

  const clearWishlist = () => {
    setWishlist([]);
  };

  const openWishlist = () => setIsWishlistOpen(true);
  const closeWishlist = () => setIsWishlistOpen(false);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        wishlistCount: wishlist.length,
        isWishlistOpen,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        toggleWishlist,
        clearWishlist,
        openWishlist,
        closeWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
